/**
 * <ry> Transform Wrapper
 *
 * Allows clean, unprefixed markup inside <ry> tags.
 * Converts <accordion> to <ry-accordion>, etc.
 *
 * Usage:
 * <ry>
 *   <accordion>
 *     <accordion-item title="Foo">Content</accordion-item>
 *   </accordion>
 * </ry>
 *
 * Or with <template ry>:
 * <template ry>
 *   <accordion>...</accordion>
 * </template>
 *
 * Both prefixed and unprefixed work together:
 * <ry>
 *   <accordion>
 *     <ry-accordion-item>Already prefixed, unchanged</ry-accordion-item>
 *     <accordion-item>Gets transformed</accordion-item>
 *   </accordion>
 * </ry>
 */

// All component names that can be transformed
const RY_COMPONENTS: readonly string[] = [
  // Layout
  'page', 'header', 'main', 'footer', 'section', 'aside',
  'grid', 'stack', 'cluster', 'split', 'center',
  'nav', 'logo', 'actions',
  // Components
  'accordion', 'accordion-item',
  'tabs', 'tab',
  'modal',
  'dropdown', 'menu', 'menu-item',
  'drawer',
  'toast',
  'alert',
  'card',
  'button',
  'badge',
  'field',
  'fields',
  'form',
  'switch',
  'tooltip',
  'select', 'option',
  'theme-toggle',
  'divider',
  'icon',
  'example',
  'toggle-button',
  'slider',
  'knob',
  'number-select',
  'color-picker',
  'color-input',
  'gradient-picker',
  'tree',
  'tree-item',
  'tag',
  'tag-input',
  'hero',
  'stat',
  'feature',
  'feature-grid',
  'pricing',
  'pricing-card',
  'carousel',
  'combobox',
  'theme-panel',
  'testimonial',
  'heading',
  'search-list',
  'search-item',
  'search-group',
  'logo-bar',
  'sidenav',
  'tile-radio',
  'tile-checkbox',
  'checkbox',
  'radio',
  'radio-group',
  'button-group',
  'progress',
  'breadcrumb',
  'loader',
  'list',
  'placeholder',
  'rating',
  'step',
  'sticky',
  'image',
  'reveal',
  'embed',
  'table',
  'nag',
  'popover',
  'context-menu',
  'hover-card',
  'calendar',
  // NOTE: 'source' is deliberately NOT here. <source> is a real HTML element
  // inside <video>/<audio>/<picture>, and rewriting it to <ry-source> corrupts
  // valid media markup. Write <ry-source> explicitly, even inside <ry>.
  // Ported from shadcn/ui
  'questionnaire',
  'question',
  'choice',
  // Ported Tier-2 utilities
  'format-bytes',
  'format-date',
  'format-number',
  'relative-time',
  'copy-button',
  'kbd',
  'countdown',
  'status',
  // Ported Tier-1 components (badge already listed above)
  'pagination',
  'progress-ring',
  'avatar',
  'timeline',
  'timeline-item',
  'chat',
  'chat-bubble',
  // Ported mockup chrome
  'browser-mockup',
  'window-mockup',
  'phone-mockup',
  // Ported Wave 4
  'dock',
  'dock-item',
  'fab',
  'fab-action',
  'filter',
  'filter-option',
  'stack-overlay',
  'n-split',
  'bento',
  'image-comparer',
  'segment',
  'segments',
  'comment',
  'comments'
] as const;

// Build regex once - matches tags not already prefixed
const TRANSFORM_REGEX = new RegExp(
  `<(/?)(${RY_COMPONENTS.join('|')})(\\s|>|/)`,
  'g'
);

/**
 * Transform unprefixed markup to ry- prefixed
 */
export function transform(html: string): string {
  return html.replace(TRANSFORM_REGEX, '<$1ry-$2$3');
}

/**
 * Process all <ry> wrappers and <template ry> blocks
 */
export function processTransforms(): void {
  // Process <ry> elements
  document.querySelectorAll('ry').forEach(el => {
    const transformed = transform(el.innerHTML);
    const template = document.createElement('template');
    template.innerHTML = transformed;
    el.replaceWith(template.content);
  });

  // Process <template ry> elements
  document.querySelectorAll('template[ry]').forEach(el => {
    const transformed = transform((el as HTMLTemplateElement).innerHTML);
    const template = document.createElement('template');
    template.innerHTML = transformed;
    el.replaceWith(template.content);
  });
}

// Run transforms when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', processTransforms);
} else {
  // DOM already loaded, run immediately
  processTransforms();
}
